import classNames from 'classnames';
import * as React from 'react';

import styles from './Heading.module.less';

interface HeadingProps extends React.HTMLAttributes<HTMLHeadingElement> {
    element: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'p';
    stylingElement?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
    fontFamilySecondary?: boolean;
    color?: 'coopGreen' | 'darkGreen' | 'white' | 'black' | 'colorGreen2';
    alignment?: 'start' | 'center' | 'end';
}

const Heading = React.forwardRef<HTMLHeadingElement, React.PropsWithChildren<HeadingProps>>(
    (props, ref) => {
        if (!props.children) return null;

        // eslint-disable-next-line unused-imports/no-unused-vars
        const { fontFamilySecondary, stylingElement, element, className, alignment, ...rest } =
            props;

        const alignStyles: React.CSSProperties = {
            width: '100%',
        };
        switch (alignment) {
            case 'start': {
                alignStyles.textAlign = 'left';
                break;
            }
            case 'end': {
                alignStyles.textAlign = 'right';
                break;
            }
            case 'center':
            default: {
                alignStyles.textAlign = 'center';
                break;
            }
        }
        return (
            <props.element
                ref={ref}
                className={classNames(
                    styles.Heading,
                    stylingElement && `Heading--${stylingElement}`,
                    props.color ? styles[`Heading--${props.color}`] : styles['Heading--black'],
                    fontFamilySecondary && styles.FontFamilySecondary,
                    className,
                )}
                style={{
                    ...rest.style,
                    ...alignStyles,
                }}
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...rest}
            >
                {props.children}
            </props.element>
        );
    },
);

interface PageHeadingProps extends Omit<HeadingProps, 'element'> {
    visuallyHidden?: boolean;
}

export const PageHeading = React.forwardRef<
    HTMLHeadingElement,
    React.PropsWithChildren<PageHeadingProps>
>((props, ref) => {
    return (
        <Heading
            ref={ref}
            element="h1"
            tabIndex={-1}
            alignment={props.alignment}
            stylingElement={props.stylingElement}
            fontFamilySecondary={props.fontFamilySecondary}
            className={classNames(
                props.className,
                props.visuallyHidden && 'u-hiddenVisually',
                'u-paddingBxxsm',
                'u-textWeightBold',
            )}
            color={props.color}
        >
            {props.children}
        </Heading>
    );
});

export default Heading;
